import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, ArrowLeft, Search } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bp_admin_token";

const TEMP_COLORS = {
    HOT: "bg-[#B15A5A] text-white",
    WARM: "bg-[#C48A5A] text-white",
    NURTURE: "bg-[#EFEBE4] text-[#4A4947]",
};

function Login({ onLogin }) {
    const [pw, setPw] = useState("");
    const [err, setErr] = useState(false);
    const [busy, setBusy] = useState(false);

    const tryLogin = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr(false);
        try {
            await axios.get(`${API}/blueprint/admin/leads`, {
                headers: { Authorization: `Bearer ${pw}` },
            });
            localStorage.setItem(TOKEN_KEY, pw);
            onLogin(pw);
        } catch {
            setErr(true);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="pt-24 min-h-[80vh] flex items-center">
            <div className="max-w-md w-full mx-auto px-6">
                <p className="ssh-eyebrow">Admin</p>
                <h1 className="mt-6 font-serif font-light tracking-tight leading-tight text-4xl text-[#2C2B29]">
                    Admin <em className="italic text-[#A68A6B]">Dashboard</em>
                </h1>
                <form onSubmit={tryLogin} className="mt-10 space-y-5">
                    <div className="space-y-2">
                        <Label className="ssh-eyebrow text-[#75736F]">Password</Label>
                        <Input
                            type="password"
                            value={pw}
                            onChange={(e) => setPw(e.target.value)}
                            className="bg-white border-[#EFEBE4] rounded-none h-12 focus-visible:ring-[#8A9A86]"
                            data-testid="bp-admin-password"
                            autoFocus
                        />
                    </div>
                    {err && (
                        <p className="text-sm text-[#B15A5A]">Incorrect password.</p>
                    )}
                    <Button
                        type="submit"
                        disabled={busy}
                        className="ssh-btn-primary rounded-none px-9 py-6 text-xs uppercase tracking-[0.22em]"
                        data-testid="bp-admin-login"
                    >
                        {busy ? "Verifying…" : "Enter"}
                    </Button>
                </form>
            </div>
        </div>
    );
}

function LeadDetail({ lead, onBack }) {
    const isCareer = lead.assessment_type === "career";
    const scoreRows = isCareer
        ? [
              ["Career Alignment", lead.career_misalignment_percentage],
              ["Career Impact", lead.career_distress_percentage],
              ["Ability to Move Forward", lead.transition_stuckness_percentage],
              ["Readiness", lead.transition_readiness_percentage],
          ]
        : [
              ["Disconnection", lead.disconnection_percentage],
              ["Distress", lead.distress_percentage],
              ["Stuckness", lead.stuckness_percentage],
              ["Readiness", lead.readiness_percentage],
          ];
    return (
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16">
            <button
                onClick={onBack}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#A68A6B] hover:text-[#897157]"
            >
                <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
                All leads
            </button>
            <div className="mt-8 flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <p className="ssh-eyebrow">
                        {isCareer ? "Career Blueprint Lead" : "Relationship Blueprint Lead"}
                    </p>
                    <h2 className="mt-3 font-serif text-3xl text-[#2C2B29]">
                        {lead.first_name}
                    </h2>
                    <p className="mt-2 text-sm text-[#4A4947]">
                        {lead.email} · {lead.country_code} {lead.phone}
                    </p>
                    <p className="mt-1 text-xs text-[#75736F]">
                        Submitted {new Date(lead.created_at).toLocaleString()}
                    </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <span
                        className={`text-[10px] uppercase tracking-[0.22em] px-3 py-1 ${TEMP_COLORS[lead.lead_temperature] || TEMP_COLORS.NURTURE}`}
                    >
                        {lead.lead_temperature}
                    </span>
                    <span className="font-serif italic text-2xl text-[#A68A6B]">
                        {lead.final_stage}
                    </span>
                </div>
            </div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
                {scoreRows.map(([label, pct]) => (
                    <div
                        key={label}
                        className="border border-[#EFEBE4] p-5 bg-white"
                    >
                        <p className="ssh-eyebrow">{label}</p>
                        <p className="mt-3 font-serif text-3xl text-[#2C2B29]">
                            {pct ?? "—"}%
                        </p>
                    </div>
                ))}
            </div>

            <div className="mt-10">
                <p className="ssh-eyebrow">
                    Q19 · {isCareer ? "Career challenges" : "What they want to change"}
                </p>
                {(lead.q19 || []).length ? (
                    <ul className="mt-4 flex flex-wrap gap-2">
                        {(lead.q19 || []).map((c) => (
                            <li
                                key={c}
                                className="text-xs uppercase tracking-[0.14em] text-[#2C2B29] border border-[#EFEBE4] px-3 py-2 bg-[#FAF8F5]"
                            >
                                {c}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="mt-3 text-sm text-[#75736F]">—</p>
                )}
            </div>
            <div className="mt-8">
                <p className="ssh-eyebrow">Q20 · Support intent</p>
                <p className="mt-3 text-base text-[#2C2B29]">
                    {lead.q20 || "—"}
                </p>
            </div>

            <div className="mt-10">
                <p className="ssh-eyebrow">Full answers (Q1 · Q18)</p>
                <table className="mt-4 w-full text-sm">
                    <tbody>
                        {Array.from({ length: 18 }, (_, i) => i + 1).map((n) => (
                            <tr
                                key={n}
                                className="border-b border-[#EFEBE4]"
                            >
                                <td className="py-2 pr-4 text-[#75736F] w-20">
                                    Q{n}
                                </td>
                                <td className="py-2 text-[#2C2B29]">
                                    {lead[`q${n}`] === null || lead[`q${n}`] === undefined
                                        ? "—"
                                        : lead[`q${n}`]}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const OPTION_LABELS = {
    journal: "Journal — ₹749",
    journal_stickers: "Journal + Sticker Pack — ₹849",
    not_sure: "Not sure yet",
};

function JournalInterestPanel({ interests }) {
    const counts = useMemo(() => {
        const c = { journal: 0, journal_stickers: 0, not_sure: 0 };
        (interests || []).forEach((i) => {
            if (c[i.option] !== undefined) c[i.option] += 1;
        });
        return c;
    }, [interests]);

    if (!interests) {
        return (
            <div className="mt-16 text-center">
                <Loader2
                    className="h-6 w-6 animate-spin text-[#A68A6B] mx-auto"
                    strokeWidth={1.5}
                />
            </div>
        );
    }

    const stats = [
        { label: "Total signups", value: interests.length, testId: "journal-stat-total" },
        { label: "Journal — ₹749", value: counts.journal, testId: "journal-stat-journal" },
        { label: "Journal + Sticker Pack — ₹849", value: counts.journal_stickers, testId: "journal-stat-bundle" },
        { label: "Not sure yet", value: counts.not_sure, testId: "journal-stat-unsure" },
    ];

    return (
        <div data-testid="journal-interest-panel">
            <p className="mt-8 text-sm text-[#75736F] max-w-xl leading-relaxed">
                Early interest for the Manifestation Journal first batch — use
                the split below to size your print run.
            </p>
            <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.map((s) => (
                    <div
                        key={s.label}
                        className="border border-[#EFEBE4] bg-white p-5"
                        data-testid={s.testId}
                    >
                        <p className="ssh-eyebrow">{s.label}</p>
                        <p className="mt-3 font-serif text-4xl text-[#2C2B29]">
                            {s.value}
                        </p>
                    </div>
                ))}
            </div>

            <div className="mt-10 overflow-x-auto border border-[#EFEBE4]">
                <table className="min-w-full text-sm">
                    <thead className="bg-[#FAF8F5]">
                        <tr className="text-left">
                            {["Name", "Email", "WhatsApp", "Interested in", "Joined"].map(
                                (h) => (
                                    <th
                                        key={h}
                                        className="p-3 text-xs uppercase tracking-[0.14em] text-[#75736F] font-medium whitespace-nowrap"
                                    >
                                        {h}
                                    </th>
                                ),
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {interests.map((i) => (
                            <tr
                                key={i.id}
                                className="border-t border-[#EFEBE4]"
                                data-testid={`journal-row-${i.id}`}
                            >
                                <td className="p-3 text-[#2C2B29]">{i.name}</td>
                                <td className="p-3 text-[#4A4947] whitespace-nowrap">
                                    {i.email}
                                </td>
                                <td className="p-3 text-[#4A4947] whitespace-nowrap">
                                    {i.whatsapp}
                                </td>
                                <td className="p-3 text-[#2C2B29] whitespace-nowrap">
                                    {OPTION_LABELS[i.option] || i.option}
                                </td>
                                <td className="p-3 text-[#75736F] whitespace-nowrap">
                                    {new Date(i.created_at).toLocaleDateString()}
                                </td>
                            </tr>
                        ))}
                        {interests.length === 0 && (
                            <tr>
                                <td
                                    colSpan={5}
                                    className="p-10 text-center text-sm text-[#75736F]"
                                >
                                    No one has joined the first-batch list yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function BlueprintAdminPage() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [leads, setLeads] = useState(null);
    const [interests, setInterests] = useState(null);
    const [tab, setTab] = useState("leads");
    const [error, setError] = useState(false);
    const [tempFilter, setTempFilter] = useState("ALL");
    const [stageFilter, setStageFilter] = useState("ALL");
    const [typeFilter, setTypeFilter] = useState("ALL");
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (!token) return;
        (async () => {
            try {
                const headers = { Authorization: `Bearer ${token}` };
                const [leadsRes, intRes] = await Promise.all([
                    axios.get(`${API}/blueprint/admin/leads`, { headers }),
                    axios.get(`${API}/journal-interest`, { headers }),
                ]);
                setLeads(leadsRes.data);
                setInterests(intRes.data);
            } catch {
                setError(true);
                localStorage.removeItem(TOKEN_KEY);
                setToken(null);
            }
        })();
    }, [token]);

    const stages = useMemo(() => {
        const s = new Set((leads || []).map((l) => l.final_stage));
        return ["ALL", ...s];
    }, [leads]);

    const filtered = useMemo(() => {
        if (!leads) return [];
        return leads.filter((l) => {
            if (tempFilter !== "ALL" && l.lead_temperature !== tempFilter) return false;
            if (stageFilter !== "ALL" && l.final_stage !== stageFilter) return false;
            if (typeFilter !== "ALL") {
                const t = l.assessment_type || "relationship";
                if (t !== typeFilter) return false;
            }
            if (q) {
                const s = q.toLowerCase();
                if (
                    !l.first_name?.toLowerCase().includes(s) &&
                    !l.email?.toLowerCase().includes(s) &&
                    !l.phone?.includes(s)
                )
                    return false;
            }
            return true;
        });
    }, [leads, tempFilter, stageFilter, typeFilter, q]);

    if (!token) return <Login onLogin={setToken} />;

    if (selected) return <LeadDetail lead={selected} onBack={() => setSelected(null)} />;

    return (
        <div className="pt-24 pb-24" data-testid="bp-admin">
            <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-6">
                <div className="flex items-end justify-between flex-wrap gap-6">
                    <div>
                        <p className="ssh-eyebrow">Admin</p>
                        <h1 className="mt-3 font-serif font-light tracking-tight leading-tight text-4xl text-[#2C2B29]">
                            Admin <em className="italic text-[#A68A6B]">Dashboard</em>
                        </h1>
                        {tab === "leads" && leads && (
                            <p className="mt-2 text-sm text-[#75736F]">
                                {leads.length} total · {filtered.length} shown
                            </p>
                        )}
                    </div>
                    <button
                        onClick={() => {
                            localStorage.removeItem(TOKEN_KEY);
                            setToken(null);
                        }}
                        className="text-xs uppercase tracking-[0.22em] text-[#A68A6B] hover:text-[#897157]"
                    >
                        Sign out
                    </button>
                </div>

                <div className="mt-8 flex gap-2" data-testid="admin-tabs">
                    {[
                        ["leads", "Blueprint Leads"],
                        ["journal", "Journal Interest"],
                    ].map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() => setTab(key)}
                            className={`px-5 py-3 text-xs uppercase tracking-[0.18em] border transition-colors ${
                                tab === key
                                    ? "border-[#A68A6B] bg-[#A68A6B] text-white"
                                    : "border-[#EFEBE4] bg-white text-[#4A4947] hover:border-[#A68A6B]"
                            }`}
                            data-testid={`admin-tab-${key}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {tab === "journal" && (
                    <JournalInterestPanel interests={interests} />
                )}

                {tab === "leads" && (
                <>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <Label className="ssh-eyebrow text-[#75736F]">Search</Label>
                        <div className="mt-2 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A68A6B]" />
                            <Input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Name, email, phone"
                                className="bg-white border-[#EFEBE4] rounded-none h-11 pl-10 focus-visible:ring-[#8A9A86]"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="ssh-eyebrow text-[#75736F]">Assessment</Label>
                        <Select value={typeFilter} onValueChange={setTypeFilter}>
                            <SelectTrigger className="mt-2 bg-white border-[#EFEBE4] rounded-none h-11">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                <SelectItem value="ALL">All</SelectItem>
                                <SelectItem value="relationship">Relationship</SelectItem>
                                <SelectItem value="career">Career Transition</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="ssh-eyebrow text-[#75736F]">Temperature</Label>
                        <Select value={tempFilter} onValueChange={setTempFilter}>
                            <SelectTrigger className="mt-2 bg-white border-[#EFEBE4] rounded-none h-11">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                {["ALL", "HOT", "WARM", "NURTURE"].map((v) => (
                                    <SelectItem key={v} value={v}>
                                        {v}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="ssh-eyebrow text-[#75736F]">Stage</Label>
                        <Select value={stageFilter} onValueChange={setStageFilter}>
                            <SelectTrigger className="mt-2 bg-white border-[#EFEBE4] rounded-none h-11">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                {stages.map((s) => (
                                    <SelectItem key={s} value={s}>
                                        {s}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {!leads && !error && (
                    <div className="mt-16 text-center">
                        <Loader2
                            className="h-6 w-6 animate-spin text-[#A68A6B] mx-auto"
                            strokeWidth={1.5}
                        />
                    </div>
                )}
                {error && (
                    <p className="mt-10 text-sm text-[#B15A5A]">
                        Could not load leads.
                    </p>
                )}
                {leads && (
                    <div className="mt-10 overflow-x-auto border border-[#EFEBE4]">
                        <table className="min-w-full text-sm">
                            <thead className="bg-[#FAF8F5]">                                <tr className="text-left">
                                    {[
                                        "Type",
                                        "Name",
                                        "Email",
                                        "Phone",
                                        "Date",
                                        "Stage",
                                        "C1",
                                        "C2",
                                        "C3",
                                        "C4",
                                        "Temp",
                                    ].map((h) => (
                                        <th
                                            key={h}
                                            className="p-3 text-xs uppercase tracking-[0.14em] text-[#75736F] font-medium whitespace-nowrap"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((l) => {
                                    const isCareer = l.assessment_type === "career";
                                    const c1 = isCareer ? l.career_misalignment_percentage : l.disconnection_percentage;
                                    const c2 = isCareer ? l.career_distress_percentage : l.distress_percentage;
                                    const c3 = isCareer ? l.transition_stuckness_percentage : l.stuckness_percentage;
                                    const c4 = isCareer ? l.transition_readiness_percentage : l.readiness_percentage;
                                    return (
                                    <tr
                                        key={l.id}
                                        onClick={() => setSelected(l)}
                                        className={`cursor-pointer border-t border-[#EFEBE4] hover:bg-[#FAF8F5] ${
                                            l.lead_temperature === "HOT"
                                                ? "bg-[#fef4f2]"
                                                : ""
                                        }`}
                                        data-testid={`bp-admin-row-${l.id}`}
                                    >
                                        <td className="p-3 text-xs uppercase tracking-[0.14em] text-[#A68A6B] whitespace-nowrap">
                                            {isCareer ? "Career" : "Relationship"}
                                        </td>
                                        <td className="p-3 text-[#2C2B29]">{l.first_name}</td>
                                        <td className="p-3 text-[#4A4947] whitespace-nowrap">{l.email}</td>
                                        <td className="p-3 text-[#4A4947] whitespace-nowrap">{l.country_code} {l.phone}</td>
                                        <td className="p-3 text-[#75736F] whitespace-nowrap">
                                            {new Date(l.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="p-3 text-[#2C2B29]">{l.final_stage}</td>
                                        <td className="p-3 text-[#4A4947]">{c1 ?? "—"}%</td>
                                        <td className="p-3 text-[#4A4947]">{c2 ?? "—"}%</td>
                                        <td className="p-3 text-[#4A4947]">{c3 ?? "—"}%</td>
                                        <td className="p-3 text-[#4A4947]">{c4 ?? "—"}%</td>
                                        <td className="p-3">
                                            <span
                                                className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 ${TEMP_COLORS[l.lead_temperature] || TEMP_COLORS.NURTURE}`}
                                            >
                                                {l.lead_temperature}
                                            </span>
                                        </td>
                                    </tr>
                                );})}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={11}
                                            className="p-10 text-center text-sm text-[#75736F]"
                                        >
                                            No leads match these filters yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
                </>
                )}
            </div>
        </div>
    );
}
